import { MessageDescriptor } from '@lingui/core';
import { defineMessage } from '@lingui/core/macro';
import { PartAlternativeSimilarityEnum } from '@luminovo/api-contracts';
import { compareByNumber, typeSafeObjectKeys } from '@luminovo/commons';
import { colorSystem, Flexbox, Text, Tooltip } from '@luminovo/design-system';
import { transEnum } from '@luminovo/frontend-utils';

import { SimilarityTag } from '../tags';

export const similarityScoreDescriptionTranslation: Record<PartAlternativeSimilarityEnum, MessageDescriptor> = {
    [PartAlternativeSimilarityEnum.SuggestedBySupplier]: defineMessage({
        message:
            'Alternative parts that were suggested by one or more suppliers (e.g. via a quote response where the offered part differed from the requested one).',
    }),
    [PartAlternativeSimilarityEnum.FormFitFunction]: defineMessage({
        message:
            'Alternative parts that have the same function, mounting technology and number of pins as a selected part option (same form, fit and function).',
    }),
    [PartAlternativeSimilarityEnum.Functional]: defineMessage({
        message:
            'Alternative parts that have the same function as a selected part option, but may have a different mounting technology or number of pins (different form and fit).',
    }),
    [PartAlternativeSimilarityEnum.UsedByOthers]: defineMessage({
        message: 'Alternative parts that have been used in previous real-world PCBA designs.',
    }),
};

const priority: Record<PartAlternativeSimilarityEnum, number> = {
    [PartAlternativeSimilarityEnum.SuggestedBySupplier]: 0,
    [PartAlternativeSimilarityEnum.UsedByOthers]: 1,
    [PartAlternativeSimilarityEnum.FormFitFunction]: 2,
    [PartAlternativeSimilarityEnum.Functional]: 3,
};

export function SimilarityTooltip({ children }: { children: React.ReactElement }): JSX.Element {
    return (
        <Tooltip variant="white" arrow title={<SimilarityTooltipTitle />}>
            {children}
        </Tooltip>
    );
}

export const SimilarityTooltipTitle = (): JSX.Element => {
    return (
        <Flexbox
            gap={4}
            sx={{
                flexDirection: 'column',
            }}
        >
            {typeSafeObjectKeys(PartAlternativeSimilarityEnum)
                .sort(compareByNumber((score) => priority[score]))
                .map((similarity, index) => {
                    return (
                        <Flexbox
                            gap="8px"
                            key={index}
                            sx={{
                                flexDirection: 'column',

                                borderBottom:
                                    index + 1 === typeSafeObjectKeys(PartAlternativeSimilarityEnum).length
                                        ? 'unset'
                                        : `1px solid ${colorSystem.neutral[2]}`,

                                padding: '16px',
                            }}
                        >
                            <span>
                                <SimilarityTag similarity={PartAlternativeSimilarityEnum[similarity]} />
                            </span>
                            <Text variant="body-small" color={colorSystem.neutral[8]}>
                                {transEnum(similarity, similarityScoreDescriptionTranslation)}
                            </Text>
                        </Flexbox>
                    );
                })}
        </Flexbox>
    );
};
